import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";
import { BYTES_IN_MB } from "constants/common";
import { useRef, useState } from "react";

export type VideoCompressorStatus = "idle" | "loading" | "compressing";

const VIDEO_TARGET_SIZE_BYTES = 20 * BYTES_IN_MB;
const VIDEO_COMPRESS_BITRATE_THRESHOLD = 1 * BYTES_IN_MB;
const AUDIO_BITRATE_KBPS = 128;
const MAX_VIDEO_BITRATE_KBPS = 4500;
const MIN_VIDEO_BITRATE_KBPS = 1500;
const FALLBACK_VIDEO_BITRATE_KBPS = 3000;

const getTargetCrf = (duration: number): number => {
  if (!duration || duration <= 30) return 20;
  if (duration <= 60) return 22;
  if (duration <= 180) return 23;
  return 24;
};

const getVideoDuration = async (file: File): Promise<number> => {
  return await new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    const url = URL.createObjectURL(file);
    const cleanup = () => URL.revokeObjectURL(url);
    video.onloadedmetadata = () => {
      const { duration } = video;
      cleanup();
      resolve(Number.isFinite(duration) && duration > 0 ? duration : 0);
    };
    video.onerror = () => {
      cleanup();
      resolve(0);
    };
    video.src = url;
  });
};

const shouldCompressVideo = async (
  file: File,
  duration: number
): Promise<boolean> => {
  if (file.size >= VIDEO_TARGET_SIZE_BYTES) return true;
  if (!duration) return false;
  return file.size / duration > VIDEO_COMPRESS_BITRATE_THRESHOLD;
};

const calculateMaxBitrateKbps = (duration: number): number => {
  if (!duration || duration <= 0) return FALLBACK_VIDEO_BITRATE_KBPS;

  const totalKbps = ((VIDEO_TARGET_SIZE_BYTES * 8) / duration / 1000) * 0.95;
  const videoKbps = Math.round(totalKbps - AUDIO_BITRATE_KBPS);

  return Math.max(
    MIN_VIDEO_BITRATE_KBPS,
    Math.min(MAX_VIDEO_BITRATE_KBPS, videoKbps)
  );
};

const useVideoCompressor = () => {
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const [status, setStatus] = useState<VideoCompressorStatus>("idle");
  const [progress, setProgress] = useState(0);

  const loadFFmpeg = async () => {
    if (ffmpegRef.current) return ffmpegRef.current;

    setStatus("loading");
    setProgress(0);

    const ffmpeg = new FFmpeg();
    // Use the ESM build over plain HTTPS — `@ffmpeg/ffmpeg@0.12` creates a
    // `type: "module"` worker, so `importScripts()` is unavailable and the
    // library falls back to a dynamic `import()` of the core. If we wrap the
    // URL with `toBlobURL` the worker can't replace `/umd/` → `/esm/`
    // (the path segment is gone) and `import()` pulls UMD as ESM, yielding
    // `failed to import ffmpeg-core.js`. jsdelivr serves the ESM build with
    // permissive CORS, so the worker can import it directly.
    const baseURL = "https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/esm";

    await ffmpeg.load({
      coreURL: `${baseURL}/ffmpeg-core.js`,
      wasmURL: `${baseURL}/ffmpeg-core.wasm`
    });

    ffmpegRef.current = ffmpeg;
    return ffmpeg;
  };

  const compressVideo = async (file: File, duration: number): Promise<File> => {
    const ffmpeg = await loadFFmpeg();

    setStatus("compressing");
    setProgress(0);

    ffmpeg.on("progress", ({ progress: p }) => {
      setProgress(Math.round(Math.max(0, Math.min(1, p)) * 100));
    });

    const inputName = "input.mp4";
    const outputName = "output.mp4";

    await ffmpeg.writeFile(inputName, await fetchFile(file));

    const crf = getTargetCrf(duration);
    const maxBitrateKbps = calculateMaxBitrateKbps(duration);
    const maxrateKbps = Math.round(maxBitrateKbps * 1.5);
    const bufsizeKbps = maxBitrateKbps * 2;

    await ffmpeg.exec([
      "-i",
      inputName,
      "-vf",
      "scale='if(gte(iw,ih),-2,min(720,iw))':'if(gte(iw,ih),min(720,ih),-2)'",
      "-c:v",
      "libx264",
      "-crf",
      String(crf),
      "-maxrate",
      `${maxrateKbps}k`,
      "-bufsize",
      `${bufsizeKbps}k`,
      "-preset",
      "fast",
      "-c:a",
      "aac",
      "-b:a",
      `${AUDIO_BITRATE_KBPS}k`,
      "-movflags",
      "+faststart",
      "-y",
      outputName
    ]);

    const data = await ffmpeg.readFile(outputName);
    const blob = new Blob([data], { type: "video/mp4" });

    await ffmpeg.deleteFile(inputName);
    await ffmpeg.deleteFile(outputName);

    return new File([blob], file.name.replace(/\.\w+$/, ".mp4"), {
      type: "video/mp4"
    });
  };

  const compressVideos = async (files: File[]): Promise<File[]> => {
    const result = await Promise.all(
      files.map(async (file) => {
        if (!file.type.startsWith("video")) return file;

        const duration = await getVideoDuration(file);
        if (!(await shouldCompressVideo(file, duration))) return file;

        const compressed = await compressVideo(file, duration);
        if (compressed.size < file.size) return compressed;
        return file;
      })
    );

    setStatus("idle");
    setProgress(0);
    return result;
  };

  return { status, progress, compressVideos };
};

export default useVideoCompressor;
